import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode } from "vue"

const _hoisted_1 = {
  class: "overflow-hidden rounded-[10px] bg-white shadow-sm ring-1 ring-gray-300",
  "aria-hidden": "true"
}
const _hoisted_2 = { class: "flex items-center gap-[5px] border-b border-gray-200 bg-[#eef1f5] px-2.5 py-[7px]" }
const _hoisted_3 = { class: "ml-2 min-w-0 truncate rounded-full bg-white px-2.5 py-0.5 text-[9.5px] leading-tight text-gray-400" }
const _hoisted_4 = { class: "mb-[11px] flex items-center gap-1.5" }
const _hoisted_5 = { class: "min-w-0 truncate font-serif text-[10px] italic text-gray-900" }
const _hoisted_6 = ["src"]
const _hoisted_7 = { class: "mb-0.5 truncate text-[8px] font-medium leading-tight" }
const _hoisted_8 = { class: "mb-[5px]" }
const _hoisted_9 = { class: "my-1.5 flex items-start gap-1 text-[7px] leading-snug opacity-80" }
const _hoisted_10 = { class: "line-clamp-2" }
const _hoisted_11 = { class: "min-w-0" }
const _hoisted_12 = { class: "flex flex-col gap-1 p-1.5" }

import { SparklesIcon } from '@heroicons/vue/20/solid'
import { computed } from 'vue'

import { FORM_COLOR_ELEMENTS, luminance, normalizeHex, shownColor } from './websiteColors'

const HONEY = '#E2A33C'
const INK = '#1B2420'

export default {
  __name: 'SiteMini',
  props: {
  channel: { type: String, required: true },
  config: { type: Object, required: true },
  siteName: { type: String, default: '' },
  // The page the form is imagined to sit on. Only ever decoration, but the wrong
  // word here makes the thumbnail read as somebody else's feature.
  path: { type: String, default: 'catering' },
  defaults: { type: Object, default: () => ({}) }
},
  setup(__props) {

const props = __props

const FORM_SLOTS = FORM_COLOR_ELEMENTS.flatMap(element => element.slots)

const tone = computed(() => props.config.colors ?? {})

const blend = (from, to, amount) => `color-mix(in srgb, ${from}, ${to} ${Math.round(amount * 100)}%)`

const formTone = key =>
  shownColor(
    tone.value,
    FORM_SLOTS.find(slot => slot.key === key)
  )

const name = computed(() => props.siteName.trim() || 'Your business')

const domain = computed(() => {
  const slug = props.siteName
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '')
  return `${slug || 'yourwebsite'}.com`
})

const url = computed(() => (props.channel === 'form' ? `${domain.value}/${props.path}` : domain.value))

const lines = computed(() => (props.channel === 'form' ? ['82%', '62%'] : ['82%', '62%', '82%', '62%']))

const labels = computed(() =>
  (props.config.questions ?? [])
    .map(question => question.label?.trim())
    .filter(Boolean)
    .slice(0, 2)
)

const flag = computed(() =>
  String(props.config.default_country || '')
    .toUpperCase()
    .replace(/[A-Z]/g, letter => String.fromCodePoint(127397 + letter.charCodeAt(0)))
)

const align = computed(() => ({ title: 'left', description: 'left', button: 'left', ...props.config.alignment }))

const formStyle = computed(() => ({ color: formTone('text'), background: formTone('background') }))
const fieldStyle = computed(() => ({ background: formTone('field') }))
const buttonStyle = computed(() => ({ background: formTone('button'), color: formTone('button_text') }))

const thread = computed(() => [
  { text: props.config.chat_opening_line?.trim() || '…' },
  { text: 'Lunch for 40', mine: true },
  { text: 'Nice. What date is it?' }
])

const accent = computed(() => normalizeHex(tone.value.accent))

const accentDeep = computed(() => {
  if (!accent.value || accent.value === normalizeHex(props.defaults.accent)) return HONEY
  return luminance(accent.value) > 0.35 ? blend(accent.value, '#000000', 0.35) : accent.value
})

const markStyle = computed(() => ({
  background: `linear-gradient(to bottom right, ${tone.value.accent}, ${blend(tone.value.accent, '#FFFFFF', 0.45)})`,
  color: accent.value && luminance(accent.value) <= 0.45 ? '#FFFFFF' : INK
}))

const avatarStyle = computed(() => ({ background: blend(tone.value.accent, '#FFFFFF', 0.8), color: accentDeep.value }))
const headLine = computed(() => blend(tone.value.header, tone.value.header_text, 0.1))
const footLine = computed(() => blend(tone.value.background, tone.value.agent_text, 0.08))
const faint = computed(() => blend(tone.value.agent, tone.value.agent_text, 0.55))

const themStyle = computed(() => ({
  background: tone.value.agent,
  color: tone.value.agent_text,
  border: `1px solid ${blend(tone.value.agent, tone.value.agent_text, 0.07)}`
}))

const mineStyle = computed(() => ({ background: tone.value.visitor, color: tone.value.visitor_text }))

const composerStyle = computed(() => ({
  background: tone.value.agent,
  borderColor: blend(tone.value.agent, tone.value.agent_text, 0.12)
}))

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(3, (dot) => {
        return _createElementVNode("span", {
          key: dot,
          class: "h-[7px] w-[7px] shrink-0 rounded-full bg-gray-300"
        })
      }), 64)),
      _createElementVNode("span", _hoisted_3, _toDisplayString(url.value), 1)
    ]),
    _createElementVNode("div", {
      class: _normalizeClass(["relative p-3", __props.channel === 'chat' ? 'min-h-[214px]' : 'min-h-[186px]'])
    }, [
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("span", _hoisted_5, _toDisplayString(name.value), 1),
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(3, (link) => {
          return _createElementVNode("span", {
            key: link,
            class: _normalizeClass(["h-1 w-[22px] shrink-0 rounded-sm bg-gray-200", link === 1 && 'ml-auto'])
          }, null, 2)
        }), 64))
      ]),
      _cache[2] || (_cache[2] = _createElementVNode("div", { class: "mb-[9px] h-[38px] rounded-[5px] bg-gradient-to-b from-slate-100 to-[#e8edf3]" }, null, -1)),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(lines.value, (width, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: index,
          class: "mb-1.5 h-[5px] rounded-[3px] bg-[#eef1f5]",
          style: _normalizeStyle({ width })
        }, null, 4))
      }), 128)),
      (__props.channel === 'form')
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "mt-2.5 rounded-md p-[9px] shadow-[0_2px_8px_rgba(15,23,42,0.07)] ring-1 ring-gray-200",
            style: _normalizeStyle(formStyle.value)
          }, [
            (__props.config.logo_url)
              ? (_openBlock(), _createElementBlock("img", {
                  key: 0,
                  src: __props.config.logo_url,
                  alt: "",
                  "data-mini-logo": "",
                  class: "mx-auto mb-1 block h-3 w-auto max-w-[45%] object-contain"
                }, null, 8, _hoisted_6))
              : _createCommentVNode("", true),
            _createElementVNode("div", {
              class: "truncate text-[9.5px] font-semibold leading-tight",
              style: _normalizeStyle({ textAlign: align.value.title })
            }, _toDisplayString(__props.config.title), 5),
            _createElementVNode("div", {
              class: "mb-1.5 mt-0.5 truncate text-[7.5px] leading-tight opacity-70",
              style: _normalizeStyle({ textAlign: align.value.description })
            }, _toDisplayString(__props.config.description), 5),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(labels.value, (label) => {
              return (_openBlock(), _createElementBlock("div", {
                key: label,
                class: "mb-[5px]"
              }, [
                _createElementVNode("div", _hoisted_7, _toDisplayString(label), 1),
                _createElementVNode("div", {
                  class: "h-3 rounded-[3px] border border-black/20",
                  style: _normalizeStyle(fieldStyle.value)
                }, null, 4)
              ]))
            }), 128)),
            _createElementVNode("div", _hoisted_8, [
              _cache[0] || (_cache[0] = _createElementVNode("div", { class: "mb-0.5 text-[8px] font-medium leading-tight" }, "Phone number", -1)),
              _createElementVNode("div", {
                class: "flex h-3 items-center rounded-[3px] border border-black/20 px-1 text-[7px] leading-none",
                style: _normalizeStyle(fieldStyle.value)
              }, _toDisplayString(flag.value), 5)
            ]),
            _createElementVNode("div", _hoisted_9, [
              _cache[1] || (_cache[1] = _createElementVNode("span", { class: "mt-px h-[7px] w-[7px] shrink-0 rounded-sm border border-black/30 bg-white" }, null, -1)),
              _createElementVNode("span", _hoisted_10, _toDisplayString(__props.config.consent_text), 1)
            ]),
            _createElementVNode("div", {
              style: _normalizeStyle({ textAlign: align.value.button })
            }, [
              _createElementVNode("span", {
                class: "inline-block rounded-[3px] px-2 py-[5px] text-[8.5px] font-semibold leading-none",
                style: _normalizeStyle(buttonStyle.value)
              }, _toDisplayString(__props.config.submit_button_text), 5)
            ], 4)
          ], 4))
        : (_openBlock(), _createElementBlock("div", {
            key: 1,
            class: "absolute bottom-3 right-3 w-[150px] overflow-hidden rounded-[10px] font-catering-body shadow-[0_10px_24px_-8px_rgba(27,36,32,0.3)]",
            style: _normalizeStyle({ background: tone.value.background })
          }, [
            _createElementVNode("div", {
              class: "flex items-center gap-1.5 border-b px-2 py-1.5",
              style: _normalizeStyle({ background: tone.value.header, borderColor: headLine.value })
            }, [
              _createElementVNode("span", {
                class: "flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[5px]",
                style: _normalizeStyle(markStyle.value)
              }, [
                _createVNode(_unref(SparklesIcon), { class: "h-2 w-2" })
              ], 4),
              _createElementVNode("span", _hoisted_11, [
                _createElementVNode("span", {
                  class: "block truncate text-[5.5px] font-semibold uppercase leading-tight tracking-[0.14em]",
                  style: _normalizeStyle({ color: accentDeep.value })
                }, _toDisplayString(__props.config.chat_subtitle), 5),
                _createElementVNode("span", {
                  class: "block truncate text-[8px] font-bold leading-tight",
                  style: _normalizeStyle({ color: tone.value.header_text })
                }, _toDisplayString(__props.config.chat_title), 5)
              ])
            ], 4),
            _createElementVNode("div", _hoisted_12, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(thread.value, (message, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: index,
                  class: _normalizeClass(["flex items-start gap-1", message.mine && 'justify-end'])
                }, [
                  (!message.mine)
                    ? (_openBlock(), _createElementBlock("span", {
                        key: 0,
                        class: "flex h-2.5 w-2.5 shrink-0 items-center justify-center rounded-[3px]",
                        style: _normalizeStyle(avatarStyle.value)
                      }, [
                        _createVNode(_unref(SparklesIcon), { class: "h-1.5 w-1.5" })
                      ], 4))
                    : _createCommentVNode("", true),
                  _createElementVNode("span", {
                    class: "max-w-[85%] rounded-[7px] px-[5px] py-[3px] text-[6.5px] leading-snug shadow-[0_1px_2px_rgba(27,36,32,0.08)]",
                    style: _normalizeStyle(message.mine ? mineStyle.value : themStyle.value)
                  }, _toDisplayString(message.text), 5)
                ], 2))
              }), 128))
            ]),
            _createElementVNode("div", {
              class: "border-t p-1.5",
              style: _normalizeStyle({ borderColor: footLine.value })
            }, [
              _createElementVNode("div", {
                class: "flex items-center gap-1 rounded-[7px] border py-[3px] pl-1.5 pr-[3px]",
                style: _normalizeStyle(composerStyle.value)
              }, [
                _createElementVNode("span", {
                  class: "min-w-0 flex-1 truncate text-[6.5px]",
                  style: _normalizeStyle({ color: faint.value })
                }, "Type your message…", 4),
                _createElementVNode("span", {
                  class: "rounded-[4px] px-1 py-0.5 text-[6px] font-semibold leading-none",
                  style: _normalizeStyle({ background: tone.value.button, color: tone.value.button_text })
                }, " Send ", 4)
              ], 4)
            ], 4)
          ], 4))
    ], 2)
  ]))
}
}

}